/**
 * Shared customer-facing Safety condition names.
 * Display only — backend IDs, detector IDs, policy IDs, and pattern keys stay unchanged.
 *
 * Used by Overview, Investigate, Action required, Safety Cases, Safety Scans, and Signed Records.
 */

export const FORKLIFT_PEDESTRIAN_LABEL = 'Forklift-pedestrian proximity';
export const RESTRICTED_AREA_LABEL = 'Restricted-area entry';
export const PPE_PRESENCE_LABEL = 'PPE presence';
export const DEMONSTRATION_CONTROL_LABEL = 'Demonstration control';

function text(value: unknown): string {
  return String(value || '').trim();
}

function compactLabel(value: string): string {
  return value.split('·')[0].trim();
}

/** Canonical display name for a Safety condition. Never mutates stored IDs. */
export function buyerConditionDisplayName(
  label?: string | null,
  incidentType?: string | null,
): string {
  const type = text(incidentType).toLowerCase().replace(/-/g, '_');
  const raw = compactLabel(text(label));
  const lower = raw.toLowerCase();
  if (
    type === 'near_miss'
    || type === 'pedestrian_near_moving_forklift'
    || type === 'forklift_proximity'
    || type === 'pedestrian_forklift'
    || /\bnear[-\s]?miss\b/.test(lower)
    || /pedestrian activity in forklift/.test(lower)
    || /vehicle\s*\/\s*pedestrian proximity/.test(lower)
    || /forklift conflict/.test(lower)
  ) {
    return FORKLIFT_PEDESTRIAN_LABEL;
  }
  if (
    type === 'zone_violation'
    || type === 'zone_intrusion'
    || type === 'dwell_violation'
    || type === 'intrusion'
    || type === 'region_entrance'
    || /\bzone (violation|intrusion)\b/.test(lower)
    || /restricted[-\s]?zone/.test(lower)
    || /restricted-zone activity/.test(lower)
    || /restricted-area (entry|activity)/.test(lower)
    || /danger[-\s]?zone/.test(lower)
  ) {
    return RESTRICTED_AREA_LABEL;
  }
  if (
    type === 'ppe_violation'
    || type === 'ppe_compliance'
    || type === 'ppe_violation_person'
    || /\bppe\b/.test(lower)
  ) {
    return PPE_PRESENCE_LABEL;
  }
  return raw || 'Safety condition';
}

/** Supporting line under the condition title. Null when it would duplicate the title. */
export function buyerConditionSubtitle(
  label?: string | null,
  incidentType?: string | null,
  conditionFamily?: string | null,
): string | null {
  const display = buyerConditionDisplayName(label, incidentType);
  if (display === FORKLIFT_PEDESTRIAN_LABEL) return null;
  if (display === RESTRICTED_AREA_LABEL) return null;
  if (display === PPE_PRESENCE_LABEL) return null;
  const family = text(conditionFamily).replace(/_/g, ' ');
  if (family && !/policy/i.test(family)) {
    return family.replace(/\b\w/g, (letter) => letter.toUpperCase());
  }
  return null;
}

/**
 * Engineering / freeze-probe titles that should not appear in customer Safety Management lists.
 * Does not mutate hosted records — display filter only.
 */
export function isEngineeringDemoArtifact(title?: string | null): boolean {
  const value = text(title);
  if (!value) return false;
  const lower = value.toLowerCase();
  if (/phase\s*2b/i.test(value)) return true;
  if (/ux activation/i.test(value)) return true;
  if (/49ff2b21/i.test(value)) return true;
  if (/start measuring occupancy/i.test(value)) return true;
  if (/ux start measuring/i.test(value)) return true;
  if (/occupancy room a/i.test(value)) return true;
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)) return true;
  if (/\b[0-9a-f]{8}\b/i.test(lower) && /activation|acceptance|occupancy/.test(lower)) return true;
  return false;
}

/** Map demo login identities to customer-facing owner labels. Does not invent people. */
export function buyerOwnerDisplayName(owner?: string | null): string {
  const value = text(owner);
  if (!value || /^(unassigned|not assigned|none)$/i.test(value)) return 'Unassigned';
  if (/^pilot_site_admin$/i.test(value)) return 'Safety Manager';
  if (/^site_admin$/i.test(value) || /^ehs_manager$/i.test(value)) return 'Safety Manager';
  if (/^operations?_manager$/i.test(value)) return 'Operations Manager';
  return value;
}

/** Policy/detector/camera IDs that must not be used as customer titles. */
export function looksLikeInternalSafetyId(value?: string | null): boolean {
  const raw = text(value);
  if (!raw || /\s/.test(raw)) return false;
  if (/^(pol|cam|dev|edge|wf|sc|rpt|det)_/i.test(raw)) return true;
  if (/_[0-9a-f]{6,}$/i.test(raw)) return true;
  return false;
}

/** Display name for a scheduled Safety Scan definition. Does not mutate stored names. */
export function buyerScanDefinitionDisplayName(
  name?: string | null,
  incidentType?: string | null,
  scanRole?: string | null,
): string {
  const raw = text(name);
  if (!raw) return 'Safety Scan';
  const roleAndName = `${text(scanRole)} ${raw}`;
  if (/a-series acceptance/i.test(raw) || /walkway.*acceptance/i.test(raw)) {
    return /follow-?up/i.test(roleAndName) ? 'Walkway follow-up measurement' : 'Walkway baseline measurement';
  }
  if (/walkway baseline/i.test(raw)) return 'Walkway baseline measurement';
  if (/walkway follow-?up/i.test(raw)) return 'Walkway follow-up measurement';
  const catalogued = buyerConditionDisplayName(raw, incidentType);
  if (catalogued && catalogued !== 'Safety condition' && !looksLikeInternalSafetyId(catalogued)) {
    return catalogued;
  }
  return raw;
}

/** Demo control names that would imply a physical change. Display mapping only. */
export function buyerControlDisplayName(name?: string | null): string {
  const value = text(name);
  if (!value) return '';
  if (
    /^walkway barrier$/i.test(value)
    || /barrier installed/i.test(value)
    || /fixed pedestrian barrier/i.test(value)
    || /training completed/i.test(value)
    || /traffic changed/i.test(value)
  ) {
    return DEMONSTRATION_CONTROL_LABEL;
  }
  return value;
}

export function buyerOverviewActionReason(workState?: string | null, itemLabel?: string | null): string {
  const raw = text(workState) || text(itemLabel) || 'Action required';
  if (/^needs attention$/i.test(raw)) return 'Action required';
  if (/^insufficient coverage$/i.test(raw)) return 'Not measured · insufficient usable coverage';
  return raw;
}

function formatHours(hours: number): string {
  return Number.isInteger(hours) ? String(hours) : hours.toFixed(1);
}

function formatCount(count: number): string {
  return count.toLocaleString('en-US');
}

export type PersistedScanMeasurement = {
  customerScanResult?: string | null;
  occurrenceCount?: number | null;
  usableHours?: number | null;
  ratePerUsableHour?: number | null;
  workflowState?: string | null;
  coverageInsufficient?: boolean;
};

function isWorkflowOnlyResult(value: string): boolean {
  return /^(signed|draft|awaiting sign-off|failed|running|queued|scheduled|insufficient|—|-)$/i.test(value);
}

/** Result cell: persisted primary + secondary counts. Never computes rate in the client. */
export function formatPersistedScanResult(input: PersistedScanMeasurement): string {
  const workflow = text(input.workflowState).toLowerCase();
  if (workflow === 'insufficient coverage' || input.coverageInsufficient) {
    return 'Not measured · insufficient usable coverage';
  }
  if (workflow === 'failed') return 'Failed';

  const persisted = text(input.customerScanResult);
  const rate = typeof input.ratePerUsableHour === 'number' && Number.isFinite(input.ratePerUsableHour)
    ? input.ratePerUsableHour
    : null;
  const occurrences = typeof input.occurrenceCount === 'number' && Number.isFinite(input.occurrenceCount)
    ? input.occurrenceCount
    : null;
  const hours = typeof input.usableHours === 'number' && Number.isFinite(input.usableHours) && input.usableHours > 0
    ? input.usableHours
    : null;

  let primary = '';
  if (persisted && !isWorkflowOnlyResult(persisted)) {
    primary = persisted;
  } else if (rate != null) {
    const rateLabel = Number.isInteger(rate) ? String(rate) : String(Number(rate.toFixed(1)));
    primary = `${rateLabel} occurrences / usable hour`;
  } else if (occurrences != null) {
    primary = `${formatCount(occurrences)} occurrence${occurrences === 1 ? '' : 's'}`;
  }

  let secondary = '';
  if (occurrences != null && hours != null) {
    secondary = `${formatCount(occurrences)} occurrence${occurrences === 1 ? '' : 's'} · ${formatHours(hours)} usable hour${hours === 1 ? '' : 's'}`;
  } else if (occurrences != null && !primary.includes('occurrence')) {
    secondary = `${formatCount(occurrences)} occurrence${occurrences === 1 ? '' : 's'}`;
  }

  if (primary && secondary && primary !== secondary) return `${primary} · ${secondary}`;
  return primary || secondary || '—';
}

/** Coverage cell: usable hours, Insufficient coverage, or No usable measurement. */
export function formatPersistedScanCoverage(input: PersistedScanMeasurement): string {
  const workflow = text(input.workflowState).toLowerCase();
  const hours = typeof input.usableHours === 'number' && Number.isFinite(input.usableHours)
    ? input.usableHours
    : null;
  if (workflow === 'insufficient coverage' || input.coverageInsufficient) {
    return 'Insufficient usable coverage';
  }
  if (hours != null && hours > 0) {
    return `${formatHours(hours)} usable hour${hours === 1 ? '' : 's'}`;
  }
  if (hours === 0) return 'No usable measurement';
  return '—';
}

export function compactCasesLiveSummary(args: {
  active: number;
  unowned: number;
  newActivity: number;
}): string {
  return `${args.active} active case${args.active === 1 ? '' : 's'} · ${args.unowned} unowned · ${args.newActivity} new activity`;
}

export function mapBuyerCaseStatusLabel(state?: string | null): string {
  const value = text(state);
  if (value === 'Closed') return 'Closed';
  if (value === 'Control recorded') return 'Control recorded';
  if (value === 'Follow-up pending' || value === 'Follow-up measuring') return 'Follow-up measuring';
  if (value === 'Monitoring' || value === 'Needs review' || value === 'Confirmed' || value === 'Reopened' || value === 'Open') {
    return 'Open';
  }
  return value || 'Open';
}
